import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Alert,
  Button,
  IconButton,
  Tooltip,
  useTheme,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Refresh as RefreshIcon,
  Share as ShareIcon,
  Launch as LaunchIcon,
} from '@mui/icons-material';
import VideoPlayer from '../../components/VideoPlayer';
import { getPipelines } from '../../api/client';

function Player() {
  const { pipelineId } = useParams();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  // State
  const [pipeline, setPipeline] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [playerError, setPlayerError] = useState(null);

  // URL parameters for direct streaming
  const hlsUrl = searchParams.get('hls');
  const dashUrl = searchParams.get('dash');
  const streamTitle = searchParams.get('title') || 'Video Stream';

  useEffect(() => {
    if (pipelineId) {
      fetchPipelineData();
    } else if (hlsUrl || dashUrl) {
      // Direct URL mode
      setLoading(false);
    } else {
      setError('No pipeline ID or stream URLs provided');
      setLoading(false);
    }
  }, [pipelineId, hlsUrl, dashUrl]);

  const fetchPipelineData = async () => {
    try {
      setLoading(true);
      const pipelines = await getPipelines();
      const foundPipeline = pipelines.find(p => p.id === pipelineId);

      if (!foundPipeline) {
        setError(`Pipeline with ID ${pipelineId} not found`);
      } else if (!foundPipeline.hls_url && !foundPipeline.dash_url) {
        setError('Pipeline has no available stream URLs');
      } else {
        setPipeline(foundPipeline);
        setError(null);
      }
    } catch (err) {
      setError(`Failed to load pipeline: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handlePlayerError = (error) => {
    setPlayerError(error);
  };

  const handleRefresh = () => {
    setPlayerError(null);
    if (pipelineId) {
      fetchPipelineData();
    }
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: pipeline?.name || streamTitle,
        url: window.location.href,
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
    }
  };

  const goBack = () => {
    window.history.back();
  };

  // Get URLs and title for the player
  const getPlayerProps = () => {
    if (pipeline) {
      return {
        hlsUrl: pipeline.hls_url,
        dashUrl: pipeline.dash_url,
        title: pipeline.name,
      };
    }
    return {
      hlsUrl,
      dashUrl,
      title: streamTitle,
    };
  };

  const playerProps = getPlayerProps();

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="60vh"
      >
        <Typography>Loading stream...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Box display="flex" alignItems="center" mb={2}>
          <IconButton onClick={goBack} sx={{ mr: 1 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5">Stream Player</Typography>
        </Box>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
        sx={{
          background: isDark
            ? 'linear-gradient(90deg, rgba(26, 26, 46, 0.8) 0%, rgba(22, 33, 62, 0.8) 100%)'
            : 'linear-gradient(90deg, #f8f9fa 0%, #e9ecef 100%)',
          p: 2,
          borderRadius: 2,
          border: isDark ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.1)',
        }}
      >
        <Box display="flex" alignItems="center">
          <IconButton onClick={goBack} sx={{ mr: 2 }}>
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="h5" component="h1">
              {playerProps.title}
            </Typography>
            {pipeline && (
              <Box display="flex" gap={1} mt={1}>
                <Chip
                  label={pipeline.type.toUpperCase()}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
                <Chip
                  label={pipeline.status}
                  size="small"
                  color={pipeline.status === 'running' ? 'success' : 'default'}
                  variant="outlined"
                />
              </Box>
            )}
          </Box>
        </Box>

        <Box display="flex" gap={1}>
          <Tooltip title="Refresh">
            <IconButton onClick={handleRefresh}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Share">
            <IconButton onClick={handleShare}>
              <ShareIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Main Video Player */}
        <Grid item xs={12} lg={9}>
          <Card>
            <CardContent sx={{ p: 0 }}>
              <VideoPlayer
                hlsUrl={playerProps.hlsUrl}
                dashUrl={playerProps.dashUrl}
                title={playerProps.title}
                autoPlay={false}
                controls={true}
                width="100%"
                height="auto"
                onError={handlePlayerError}
                onLoadStart={() => setPlayerError(null)}
              />
            </CardContent>
          </Card>

          {/* Player Error Alert */}
          {playerError && (
            <Alert
              severity="warning"
              sx={{ mt: 2 }}
              action={
                <Button onClick={handleRefresh} size="small">
                  Retry
                </Button>
              }
            >
              <Typography variant="body2">
                <strong>Playback Issue:</strong> {playerError.details || 'Unknown error'}
              </Typography>
            </Alert>
          )}
        </Grid>

        {/* Sidebar Info */}
        <Grid item xs={12} lg={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Stream Information
              </Typography>

              {/* URLs */}
              <Box mb={3}>
                {playerProps.hlsUrl && (
                  <Box mb={2}>
                    <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                      HLS Stream
                    </Typography>
                    <Box
                      sx={{
                        p: 1,
                        background: isDark ? 'rgba(0, 255, 136, 0.05)' : 'rgba(76, 175, 80, 0.05)',
                        borderRadius: 1,
                        border: isDark ? '1px solid rgba(0, 255, 136, 0.2)' : '1px solid rgba(76, 175, 80, 0.2)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          fontFamily: 'monospace',
                          wordBreak: 'break-all',
                          color: isDark ? '#00ff88' : '#4caf50',
                        }}
                      >
                        {playerProps.hlsUrl.substring(0, 50)}...
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => window.open(playerProps.hlsUrl, '_blank')}
                      >
                        <LaunchIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                )}

                {playerProps.dashUrl && (
                  <Box mb={2}>
                    <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                      DASH Stream
                    </Typography>
                    <Box
                      sx={{
                        p: 1,
                        background: isDark ? 'rgba(0, 212, 255, 0.05)' : 'rgba(25, 118, 210, 0.05)',
                        borderRadius: 1,
                        border: isDark ? '1px solid rgba(0, 212, 255, 0.2)' : '1px solid rgba(25, 118, 210, 0.2)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          fontFamily: 'monospace',
                          wordBreak: 'break-all',
                          color: isDark ? '#00d4ff' : '#1976d2',
                        }}
                      >
                        {playerProps.dashUrl.substring(0, 50)}...
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => window.open(playerProps.dashUrl, '_blank')}
                      >
                        <LaunchIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                )}
              </Box>

              {/* Pipeline Details */}
              {pipeline && (
                <Box>
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                    Pipeline Details
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>ID:</strong> {pipeline.id}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Type:</strong> {pipeline.type}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Status:</strong> {pipeline.status}
                  </Typography>
                  {pipeline.creator_username && (
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Creator:</strong> {pipeline.creator_username}
                    </Typography>
                  )}
                  {pipeline.estimated_hourly_cost && (
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Cost/hr:</strong> ${pipeline.estimated_hourly_cost}
                    </Typography>
                  )}
                </Box>
              )}

              {/* Usage Instructions */}
              <Box mt={3}>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  Usage
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  You can also access streams directly using URL parameters:
                  <br />
                  <code>?hls=URL</code> for HLS streams
                  <br />
                  <code>?dash=URL</code> for DASH streams
                  <br />
                  <code>?title=NAME</code> for custom title
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

export default Player;